import React, { useState, useEffect, useCallback } from 'react';
import { logger } from '../utils/logger';
import { 
  getCompetitors,
  getCompetitorStats,
  upsertCompetitorProfile,
  deleteCompetitorProfile,
  createTag,
  updateTag,
  getDimensions,
  createDimension
} from '../services/featureService';
import { notifyDimensionOrTagListChanged } from '../utils/dimensionTagSync';
import './Competitors.css';

const Competitors = ({ featureGroupId, userRole }) => {
  const [competitors, setCompetitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingCompetitor, setEditingCompetitor] = useState(null);
  const [stats, setStats] = useState(new Map()); // tagId -> { featureCount, score }
  const [loadingStats, setLoadingStats] = useState(new Set());
  const [needsDimensionExists, setNeedsDimensionExists] = useState(true);
  const [formData, setFormData] = useState({
    tag_name: '',
    company_name: '',
    company_url: '',
    company_summary: '',
    pricing: ''
  });
  const [errors, setErrors] = useState({});

  const isOwner = userRole === 'Owner';

  const loadStatsForCompetitors = useCallback(async (competitorsList) => {
    const statsMap = new Map();
    const loadingSet = new Set();

    for (const competitor of competitorsList) {
      loadingSet.add(competitor.id);
      try {
        const competitorStats = await getCompetitorStats(competitor.id, featureGroupId);
        statsMap.set(competitor.id, competitorStats);
      } catch (err) {
        logger.error(`Error loading stats for competitor ${competitor.id}:`, err);
        statsMap.set(competitor.id, { featureCount: 0, score: null });
      } finally {
        loadingSet.delete(competitor.id);
      }
    }

    setStats(statsMap);
    setLoadingStats(loadingSet);
  }, [featureGroupId]);

  const loadCompetitors = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getCompetitors(featureGroupId);
      setCompetitors(data || []);

      // Check if Needs dimension exists
      const dimensions = await getDimensions(featureGroupId);
      const needsDim = dimensions.find(d => d.name === 'Needs');
      setNeedsDimensionExists(!!needsDim);

      // Load stats for all competitors
      if (data && data.length > 0) {
        loadStatsForCompetitors(data);
      }
    } catch (err) {
      setError('Failed to load competitors');
      logger.error(err);
    } finally {
      setLoading(false);
    }
  }, [featureGroupId, loadStatsForCompetitors]);

  useEffect(() => {
    if (featureGroupId) {
      loadCompetitors();
    }
  }, [featureGroupId, loadCompetitors]);

  const handleAdd = () => {
    setEditingCompetitor(null);
    setFormData({
      tag_name: '',
      company_name: '',
      company_url: '',
      company_summary: '',
      pricing: ''
    });
    setErrors({});
    setShowForm(true);
  };

  const handleEdit = (competitor) => {
    setEditingCompetitor(competitor);
    setFormData({
      tag_name: competitor.name,
      company_name: competitor.profile?.company_name || '',
      company_url: competitor.profile?.company_url || '',
      company_summary: competitor.profile?.company_summary || '',
      pricing: competitor.profile?.pricing || ''
    });
    setErrors({});
    setShowForm(true);
  };

  const handleDelete = async (competitor) => {
    if (!window.confirm(`Are you sure you want to delete the competitor "${competitor.name}"?\n\nThis will delete the tag and all competitor profile data.`)) {
      return;
    }

    try {
      await deleteCompetitorProfile(competitor.id, true); // Delete tag as well
      notifyDimensionOrTagListChanged(featureGroupId);
      await loadCompetitors();
    } catch (err) {
      alert('Failed to delete competitor');
      logger.error(err);
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.tag_name.trim()) {
      newErrors.tag_name = 'Tag name is required';
    }
    if (!formData.company_name.trim()) {
      newErrors.company_name = 'Company name is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      // First, find or create the competitors dimension
      const dimensions = await getDimensions(featureGroupId);
      let competitorsDim = dimensions.find(d => d.name === 'competitors');

      if (!competitorsDim) {
        // Create competitors dimension if it doesn't exist
        competitorsDim = await createDimension({
          feature_group_id: featureGroupId,
          name: 'competitors',
          dimension_type: 'RATING',
          visible_to_guest: false,
          visible_to_editor: true,
          visible_to_owner: true,
          editable_to_guest: false,
          editable_to_editor: true,
          editable_to_owner: true
        });
      }

      if (editingCompetitor) {
        // Update existing tag
        await updateTag(editingCompetitor.id, {
          name: formData.tag_name.trim()
        });

        // Update or create profile
        await upsertCompetitorProfile(
          editingCompetitor.id,
          featureGroupId,
          {
            company_name: formData.company_name.trim(),
            company_url: formData.company_url.trim() || null,
            company_summary: formData.company_summary.trim() || null,
            pricing: formData.pricing.trim() || null
          }
        );
      } else {
        // Create new tag
        const newTag = await createTag({
          dimension_id: competitorsDim.id,
          name: formData.tag_name.trim(),
          keywords: []
        });

        // Create profile
        await upsertCompetitorProfile(
          newTag.id,
          featureGroupId,
          {
            company_name: formData.company_name.trim(),
            company_url: formData.company_url.trim() || null,
            company_summary: formData.company_summary.trim() || null,
            pricing: formData.pricing.trim() || null
          }
        );
      }

      setShowForm(false);
      setEditingCompetitor(null);
      setFormData({
        tag_name: '',
        company_name: '',
        company_url: '',
        company_summary: '',
        pricing: ''
      });
      notifyDimensionOrTagListChanged(featureGroupId);
      await loadCompetitors();
    } catch (err) {
      alert(editingCompetitor ? 'Failed to update competitor' : 'Failed to create competitor');
      logger.error(err);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingCompetitor(null);
    setFormData({
      tag_name: '',
      company_name: '',
      company_url: '',
      company_summary: '',
      pricing: ''
    });
    setErrors({});
  };

  const formatScore = (score) => {
    if (score === null) {
      return 'N/A';
    }
    return score > 0 ? `+${score}` : `${score}`;
  };

  if (!isOwner) {
    return (
      <div className="competitors-content">
        <div className="competitors-header">
          <h3>Competitors</h3>
          <p className="info-message">Only Feature Map Owners can manage competitors.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="competitors-content">
      <div className="competitors-header">
        <h3>Competitors</h3>
        <button className="add-competitor-button" onClick={handleAdd}>
          <i className="fas fa-plus"></i> Add Competitor
        </button>
      </div>

      {loading && <div className="loading">Loading competitors...</div>}
      {error && <div className="error">{error}</div>}

      {!loading && !error && (
        <>
          {!needsDimensionExists && (
            <div className="warning-message">
              Please add feature tag for Needs and set ratings.
            </div>
          )}

          {showForm && (
            <div className="competitor-form-container">
              <form onSubmit={handleSubmit} className="competitor-form">
                <h4>{editingCompetitor ? 'Edit Competitor' : 'Add New Competitor'}</h4>
                
                <div className="form-group">
                  <label>
                    Tag Name <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.tag_name}
                    onChange={(e) => setFormData({ ...formData, tag_name: e.target.value })}
                    className={errors.tag_name ? 'error' : ''}
                    placeholder="Enter tag name"
                  />
                  {errors.tag_name && <span className="error-message">{errors.tag_name}</span>}
                </div>

                <div className="form-group">
                  <label>
                    Company Name <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.company_name}
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                    className={errors.company_name ? 'error' : ''}
                    placeholder="Enter company name"
                  />
                  {errors.company_name && <span className="error-message">{errors.company_name}</span>}
                </div>

                <div className="form-group">
                  <label>Company URL</label>
                  <input
                    type="url"
                    value={formData.company_url}
                    onChange={(e) => setFormData({ ...formData, company_url: e.target.value })}
                    placeholder="https://example.com"
                  />
                </div>

                <div className="form-group">
                  <label>Company Summary</label>
                  <textarea
                    value={formData.company_summary}
                    onChange={(e) => setFormData({ ...formData, company_summary: e.target.value })}
                    placeholder="Enter company summary"
                    rows={4}
                  />
                </div>

                <div className="form-group">
                  <label>Pricing</label>
                  <textarea
                    value={formData.pricing}
                    onChange={(e) => setFormData({ ...formData, pricing: e.target.value })}
                    placeholder="Enter pricing information"
                    rows={3}
                  />
                </div>

                <div className="form-actions">
                  <button type="button" onClick={handleCancel} className="cancel-button">
                    Cancel
                  </button>
                  <button type="submit" className="save-button">
                    {editingCompetitor ? 'Update' : 'Create'} Competitor
                  </button>
                </div>
              </form>
            </div>
          )}

          {competitors.length === 0 ? (
            <div className="no-competitors">
              No competitors yet. Click "Add Competitor" to create one.
            </div>
          ) : (
            <table className="competitors-table">
              <thead>
                <tr>
                  <th>Company Name</th>
                  <th>Tag Name</th>
                  <th># Features Tagged</th>
                  <th>Score</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {competitors.map(competitor => {
                  const competitorStats = stats.get(competitor.id);
                  const isLoading = loadingStats.has(competitor.id);
                  
                  return (
                    <tr key={competitor.id}>
                      <td className="company-name">
                        {competitor.profile?.company_name || competitor.name}
                      </td>
                      <td className="tag-name">{competitor.name}</td>
                      <td className="feature-count">
                        {isLoading ? (
                          <span className="loading-stats">Loading...</span>
                        ) : (
                          competitorStats?.featureCount || 0
                        )}
                      </td>
                      <td className="score">
                        {isLoading ? (
                          <span className="loading-stats">Loading...</span>
                        ) : competitorStats?.score === null ? (
                          <span className="score-na">N/A</span>
                        ) : (
                          <span className={`score-value ${competitorStats.score >= 0 ? 'positive' : 'negative'}`}>
                            {formatScore(competitorStats.score)}
                          </span>
                        )}
                      </td>
                      <td className="competitor-actions">
                        <button
                          className="action-button"
                          onClick={() => handleEdit(competitor)}
                          title="Edit competitor"
                        >
                          <i className="fas fa-edit"></i>
                        </button>
                        <button
                          className="action-button delete"
                          onClick={() => handleDelete(competitor)}
                          title="Delete competitor"
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
};

export default Competitors;
